<template>
  <div>
    <SearchBar v-model="search" class="pa-2" />

    <v-card outlined class="rounded-xl">
      <v-toolbar flat dense height="45" color="grey lighten-3">
        <v-toolbar-title>
          <span class="subtitle-1 font-weight-regular">Location </span>
        </v-toolbar-title>
      </v-toolbar>

      <v-card-text>
        <v-data-table
          :headers="headers"
          :items="locations"
          :options.sync="pagination"
          :server-items-length="count"
          :loading="loading"
          :items-per-page="limit"
          class="elevation-1"
        >
          <template v-slot:item.name="{ item }">
            <div>
              {{ item.name }}
              <v-chip
                v-if="item.hasIntegration"
                small
                color="#50f4a3"
                text-color="green darken-3"
                class="ml-2"
              >
                <v-icon small color="green darken-3" left>mdi-loyalty</v-icon>
                Installed
              </v-chip>
            </div>
          </template>

          <template v-slot:item.action="{ item }">
            <div class="text-right">
              <div v-if="item.includedInPlan">
                <v-dialog v-model="includedDialog" max-width="500">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn text color="green" v-bind="attrs" v-on="on">
                      Included
                    </v-btn>
                  </template>
                  <v-card>
                    <v-card-text>
                      This add-on is included within this location’s plan at no
                      extra cost.
                    </v-card-text>
                    <v-divider></v-divider>
                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn
                        text
                        color="primary"
                        @click="includedDialog = false"
                      >
                        Close
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
              </div>

              <div v-else>
                <v-btn
                  v-if="!item.hasIntegration"
                  color="blue"
                  text
                  @click="enableDialogOpen(item)"
                >
                  Enable
                </v-btn>
                <v-btn v-else color="red" text @click="disableDialogOpen(item)">
                  Disable
                </v-btn>
              </div>
            </div>
          </template>
        </v-data-table>
      </v-card-text>

      <!-- Enable Integration -->
      <v-dialog v-model="enabledDialog.dialog" max-width="600">
        <v-card>
          <v-card-title class="headline"> Enable {{ app.name }}? </v-card-title>

          <v-card-text>
            Enabling this app will install {{ app.name }} for
            {{ enabledDialog.location?.name }}. {{ app.price | currency }} will
            be added to this location’s subscription.
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              text
              color="red darken-1"
              @click="enabledDialog.dialog = false"
              :disabled="enabledDialog.isLoading"
            >
              Cancel
            </v-btn>
            <v-btn
              color="primary"
              @click="enableApp"
              v-if="parseFloat(app.price) === 0 || hasPaymentMethod"
              :loading="enabledDialog.isLoading"
            >
              Agree
            </v-btn>
            <UpdatePaymentDialog v-else @update="updateHasPayment" />
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Disable Integration -->
      <v-dialog v-model="disableDialog.dialog" max-width="290">
        <v-card>
          <v-card-title class="headline">
            Disable {{ app.name }}?
          </v-card-title>

          <v-card-text>
            Disabling {{ app.name }} for {{ disableDialog.location?.name }} will
            stop this feature immediately. You will be credited for the
            remainder of the billing period on your next invoice.
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              text
              color="red darken-1"
              @click="disableDialog.dialog = false"
              :disabled="disableDialog.isLoading"
            >
              Cancel
            </v-btn>
            <v-btn
              text
              color="green darken-1"
              @click="disableApp"
              :loading="disableDialog.isLoading"
            >
              Confirm
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import marketplaceService from "../service.js";
import UpdatePaymentDialog from "../../Settings/components/Billing/components/UpdatePayment";

export default {
  name: "marketplace-app-locations",
  props: ["id", "app"],
  components: {
    UpdatePaymentDialog,
  },
  data() {
    return {
      includedDialog: false,
      headers: [
        { text: this.$t("labels.name"), value: "name", sortable: true },
        { text: "", value: "action", sortable: false, align: "end" },
      ],
      search: "",
      locations: [],
      count: 0,
      limit: 10,
      offset: 0,
      loading: false,
      pagination: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ["name"],
        sortDesc: [false],
      },
      enabledDialog: { dialog: false, location: null, isLoading: false },
      disableDialog: { dialog: false, location: null, isLoading: false },
    };
  },
  computed: {
    ...mapGetters("marketplace", ["hasPaymentMethod"]),
  },
  watch: {
    search() {
      if (this.search.length > 3 || this.search.length === 0) {
        this.getList();
      }
    },
    pagination: {
      handler() {
        this.getList();
      },
      deep: true,
    },
  },
  methods: {
    ...mapActions("marketplace", ["updateHasPayment"]),
    getList() {
      this.loading = true;
      const { page, itemsPerPage, sortBy, sortDesc } = this.pagination;

      this.offset = itemsPerPage * (page - 1);
      this.limit = itemsPerPage;

      return marketplaceService
        .getLocations(this.id, {
          limit: this.limit,
          offset: this.offset,
          search: this.search,
          orderBy: [sortBy[0], sortDesc[0] ? "DESC" : "ASC"],
        })
        .then((response) => {
          this.locations = response.data;
          this.count = response.count;
          this.loading = false;
        })
        .catch((error) => {
          console.log('error', error)
          this.$toasted.error(error.data.message, {
            position: "top-right",
            duration: 4000,
          });
        });
    },
    enableDialogOpen(location) {
      this.enabledDialog.location = location;
      this.enabledDialog.dialog = true;
    },
    enableApp() {
      this.enabledDialog.isLoading = true;
      return marketplaceService
        .enable(this.id, { LocationId: this.enabledDialog.location.id })
        .then(() => {
          this.enabledDialog = {
            dialog: false,
            location: null,
            isLoading: false,
          };
          this.getList();
        })
        .catch((error) => {
          console.error("marketplaceService", error);
          this.$toasted.error(error.data.message, { duration: 4000 });
        });
    },
    disableDialogOpen(location) {
      this.disableDialog.location = location;
      this.disableDialog.dialog = true;
    },
    disableApp() {
      this.disableDialog.isLoading = true;
      return marketplaceService
        .disable(this.id, { LocationId: this.disableDialog.location.id })
        .then(() => {
          this.getList();
        })
        .catch((error) => {
          this.$toasted.error(error?.data?.message || "Failed to disable app", {
            duration: 4000,
          });
        })
        .finally(() => {
          this.disableDialog = {
            dialog: false,
            location: null,
            isLoading: false,
          };
        });
    },
  },
};
</script>